import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Icon, IconButton, Image, Toast } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { type TwilioAccountConfig } from '~types'

import { useAISettings } from '../../AISettingsContext'

import TwilioAccountSection from './TwilioAccountSection'
import './TwilioAccountSection.scss'
import { TWILIO_LOGO_URL } from './TwilioAccountSidCode'

interface TwilioAccountsModalProps {
  open: boolean
  onClose: () => void
}

interface ToastState {
  message: string
  type: 'success' | 'error'
}

const TwilioAccountsModal: React.FC<TwilioAccountsModalProps> = ({ open, onClose }) => {
  const { selectedOrganization } = useAISettings()
  const user = useSelector((state: RootState) => state?.auth.user)
  const selectedOrganizationId = selectedOrganization?.id
  const userId = user?.id

  const [twilioAccountConfigs, setTwilioAccountConfigs] = useState<TwilioAccountConfig[]>([])
  const [hasLoaded, setHasLoaded] = useState<boolean>(false)
  const [toast, setToast] = useState<ToastState | null>(null)

  const fetchTwilioAccountConfigs = useCallback(async (): Promise<void> => {
    if (!selectedOrganizationId || !userId) return
    try {
      const response = await apiZodios.integrations_listTwilioAccountConfigs({
        user_id: userId,
        organization_identity_id: selectedOrganizationId,
      })
      setTwilioAccountConfigs(response.twilio_account_configs || [])
      setHasLoaded(true)
    } catch (error) {
      console.error('Error fetching Twilio accounts:', error)
      setToast({ message: 'Failed to load Twilio accounts', type: 'error' })
    }
  }, [selectedOrganizationId, userId])

  useEffect(() => {
    if (open) fetchTwilioAccountConfigs()
  }, [open, fetchTwilioAccountConfigs])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle className='twilio-account-dialog__header'>
        <span className='twilio-account-dialog__title'>
          <Image src={TWILIO_LOGO_URL} alt='Twilio' className='twilio-account-section__logo' />
          Twilio
        </span>
        <IconButton size='small' onClick={onClose} aria-label='Close dialog' title='Close'>
          <Icon name='close' fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TwilioAccountSection
          key={String(open)}
          twilioAccountConfigs={twilioAccountConfigs}
          hasLoaded={hasLoaded}
          onChange={fetchTwilioAccountConfigs}
          onMessage={(message, type) => setToast({ message, type })}
        />
        <Toast
          type={toast?.type ?? 'success'}
          message={toast?.message ?? ''}
          open={!!toast}
          onClose={() => setToast(null)}
          autoHideDuration={3000}
        />
      </DialogContent>
    </Dialog>
  )
}

export default TwilioAccountsModal
